import HaspObject from '../Hasp/HaspObject';

// Snap threshold in stage-content units.
const SNAP_DISTANCE = 8;

export default class SnapManager {

  /**
   * Collect snap lines from every visible sibling HaspObject of `dragging`.
   * Returns stage-content coordinates (getBounds uses relativeTo: stage).
   *
   * Each entry in vertical/horizontal carries:
   *   pos    – the snap coordinate in stage-content space
   *   bounds – the source object's full bounding rect in stage-content space
   */
  /**
   * @param {HaspObject} dragging
   * @param {GuidelineManager|null} guidelineManager  – optional, passed from HaspObjectLayer
   */
  collectSnapTargets(dragging, guidelineManager = null) {
    const vertical   = [];
    const horizontal = [];

    const parent = dragging.getParent();
    if (!parent) return { vertical, horizontal };

    // ── Sibling objects ──────────────────────────────────────────────────────
    parent.getChildren().forEach(child => {
      if (child === dragging) return;
      if (!(child instanceof HaspObject)) return;
      if (!child.visible()) return;

      const bounds = child.getBounds();
      const pts    = child.getSnapPoints();

      pts.vertical.forEach(pos   => vertical.push({ pos, bounds }));
      pts.horizontal.forEach(pos => horizontal.push({ pos, bounds }));
    });

    // ── Parent container edges ───────────────────────────────────────────────
    if (typeof parent.getBounds === 'function') {
      const pb = parent.getBounds();
      [pb.x, pb.x + pb.width / 2, pb.x + pb.width].forEach(pos =>
        vertical.push({ pos, bounds: pb })
      );
      [pb.y, pb.y + pb.height / 2, pb.y + pb.height].forEach(pos =>
        horizontal.push({ pos, bounds: pb })
      );
    }

    // ── Guidelines ──────────────────────────────────────────────────────────
    if (guidelineManager) {
      const gt = guidelineManager.getSnapTargets();
      gt.vertical.forEach(t   => vertical.push(t));
      gt.horizontal.forEach(t => horizontal.push(t));
    }

    return { vertical, horizontal };
  }

  /**
   * Find the closest snap for a node at the proposed stage-content position.
   *
   * `contentPos`  – proposed top-left of the node in stage-content space
   * `nodeSize`    – {width, height} of the node in stage-content space
   * `targets`     – result of collectSnapTargets (stage-content)
   *
   * Returns:
   *   snapX, snapY        – snapped top-left position in stage-content (default = contentPos)
   *   guideX              – absolute x of vertical alignment guide (stage-content)
   *   guideXyMin/Max      – y-extent of that guide
   *   guideY              – absolute y of horizontal alignment guide (stage-content)
   *   guideYxMin/Max      – x-extent of that guide
   */
  findSnapForContentPos(contentPos, nodeSize, targets) {
    const dragV = [
      contentPos.x,
      contentPos.x + nodeSize.width / 2,
      contentPos.x + nodeSize.width,
    ];
    const dragH = [
      contentPos.y,
      contentPos.y + nodeSize.height / 2,
      contentPos.y + nodeSize.height,
    ];

    let snapX = contentPos.x;
    let snapY = contentPos.y;
    let guideX, guideY;
    let guideXyMin, guideXyMax;
    let guideYxMin, guideYxMax;
    let minDx = SNAP_DISTANCE + 1;
    let minDy = SNAP_DISTANCE + 1;
    let snappedVerticalGuidelineId = null;
    let snappedHorizontalGuidelineId = null;

    // ── X axis ──────────────────────────────────────────────────────────────
    dragV.forEach(v => {
      targets.vertical.forEach(({ pos: tv, bounds: tb, guidelineId: tgid }) => {
        const dx = Math.abs(v - tv);
        if (dx < minDx) {
          minDx        = dx;
          guideX       = tv;
          snapX        = contentPos.x + (tv - v);
          guideXyMin   = Math.min(contentPos.y, tb.y);
          guideXyMax   = Math.max(contentPos.y + nodeSize.height, tb.y + tb.height);
          snappedVerticalGuidelineId = tgid ?? null;
        }
      });
    });

    // ── Y axis ──────────────────────────────────────────────────────────────
    dragH.forEach(h => {
      targets.horizontal.forEach(({ pos: th, bounds: tb, guidelineId: tgid }) => {
        const dy = Math.abs(h - th);
        if (dy < minDy) {
          minDy        = dy;
          guideY       = th;
          snapY        = contentPos.y + (th - h);
          guideYxMin   = Math.min(contentPos.x, tb.x);
          guideYxMax   = Math.max(contentPos.x + nodeSize.width, tb.x + tb.width);
          snappedHorizontalGuidelineId = tgid ?? null;
        }
      });
    });

    return {
      snapX, snapY,
      guideX, guideXyMin, guideXyMax,
      guideY, guideYxMin, guideYxMax,
      snappedVerticalGuidelineId,
      snappedHorizontalGuidelineId,
    };
  }

  /**
   * Find the nearest snap target for a single edge during resize.
   *
   * `edgeValue`   – the moving edge position in stage-content coords
   * `targetLines` – array of {pos, bounds} from collectSnapTargets
   *
   * Returns {snappedPos, bounds} if within SNAP_DISTANCE, otherwise undefined.
   */
  findSnapForEdge(edgeValue, targetLines) {
    let minDx = SNAP_DISTANCE + 1;
    let best  = undefined;
    targetLines.forEach(({ pos, bounds }) => {
      const dx = Math.abs(edgeValue - pos);
      if (dx < minDx) {
        minDx = dx;
        best  = { snappedPos: pos, bounds };
      }
    });
    return best;
  }
}
